import { interpret } from './intents';
import { euro } from './time';
import type { Processing, SoundId } from '../types/anna';
import type { DeskPanelKind, Tables } from '../types/db';
import type { StageEffect } from '../types/stage';

export interface DeskReply {
  say: string;
  where: Processing;
  panel?: DeskPanelKind;
  tone?: SoundId;
  concerned?: boolean;
  effect?: StageEffect;
}

/** Desk-side intent routing: work requests summon panels; everything else falls back to Anna's home intents. */
export function interpretDesk(raw: string, t: Tables): DeskReply {
  const s = ` ${raw.toLowerCase().replace(/[.,!?]/g, ' ')} `;
  const has = (re: RegExp) => re.test(s);
  const pending = t.proposals.filter((p) => p.status === 'wacht').length;

  // Safety first: locks, alarm, payments and deleting are never automatic.
  const home = interpret(raw, { mode: t.meta.mode, waiting: pending, roomIds: t.rooms.map((r) => r.id) });
  if (home.tone === 'geweigerd') {
    return { say: home.say, where: 'lokaal', tone: 'geweigerd', concerned: true, effect: home.effect };
  }

  if (has(/(diff|build|fix| pr |142|code)/)) {
    const pr = t.pullRequests[0];
    return {
      say: `Hier is de fix: ${pr.files.length} bestanden, alle ${pr.tests.passed} tests geslaagd. Mergen doe je zelf.`,
      where: 'lokaal',
      panel: 'diff',
    };
  }
  if (has(/(slack|kanaal|project-x)/)) {
    return {
      say: 'Klant Bakker vraagt in #project-x of de fix vandaag live gaat. Ik heb een antwoord voorbereid.',
      where: 'lokaal',
      panel: 'slack',
    };
  }
  if (has(/(beeld|render|studio|landingspagina|afbeelding)/)) {
    const r = t.renders[0];
    if (r.status === 'klaar') return { say: 'Hier is het beeld voor de landingspagina.', where: r.where, panel: 'render' };
    const studio = t.agentTasks.find((x) => x.agent === 'studio');
    return { say: `Studio is nog bezig: ${studio?.stepLabel ?? 'render loopt'}. Ik laat het zien zodra het klaar is.`, where: 'lokaal' };
  }
  if (has(/(kosten|budget|cloud|kost)/)) {
    const { spent, budget, localOnly } = t.meta.cloud;
    return {
      say: `Vandaag ${euro(spent)} van ${euro(budget)} aan cloud.${localOnly ? ' Rest van de dag werk ik lokaal.' : ''}`,
      where: 'lokaal',
      panel: 'kosten',
    };
  }
  if (has(/(mail|bakker|concept)/)) {
    return { say: 'Hier is het concept voor klant Bakker. Versturen doe ik pas na jouw ok.', where: 'cloud', panel: 'mail' };
  }
  if (has(/(lisa|meeting|voorbereid)/)) {
    return { say: 'Je meeting met Lisa is om 14:00. Dit is de voorbereiding.', where: 'lokaal', panel: 'lisa' };
  }

  return { say: home.say, where: home.where, effect: home.effect };
}
